import type { CharacterAsset, PropAsset, SceneAsset } from '../types';

export const sceneAssets: SceneAsset[] = [
  { id: 'studio-loft', name: 'Studio Loft', emoji: '▦', keywords: ['office', 'creative', 'loft', 'room', 'meeting'] },
  { id: 'bright-classroom', name: 'Bright Classroom', emoji: '▤', keywords: ['school', 'class', 'education', 'lesson'] },
  { id: 'cosy-lounge', name: 'Cosy Lounge', emoji: '▥', keywords: ['living room', 'home', 'sofa', 'interior'] },
  { id: 'city-crossing', name: 'City Crossing', emoji: '▧', keywords: ['city', 'street', 'buildings', 'urban'] },
  { id: 'neighbourhood-park', name: 'Neighbourhood Park', emoji: '◒', keywords: ['park', 'tree', 'outdoors', 'nature'] },
  { id: 'deep-forest', name: 'Deep Forest', emoji: '▲', keywords: ['forest', 'woods', 'trees', 'nature'] },
  { id: 'sunny-beach', name: 'Sunny Beach', emoji: '◡', keywords: ['beach', 'sea', 'summer', 'holiday'] },
  { id: 'mountain-valley', name: 'Mountain Valley', emoji: '△', keywords: ['mountain', 'valley', 'nature', 'travel'] },
  { id: 'orbiting-space', name: 'Orbiting Space', emoji: '◎', keywords: ['space', 'planet', 'stars', 'science'] },
  { id: 'modern-kitchen', name: 'Modern Kitchen', emoji: '▣', keywords: ['kitchen', 'home', 'cooking', 'food'] },
  { id: 'theatre-stage', name: 'Theatre Stage', emoji: '◢', keywords: ['stage', 'theatre', 'presentation', 'curtain'] },
  { id: 'colour-wash', name: 'Colour Wash', emoji: '◈', keywords: ['abstract', 'gradient', 'colour', 'background'] },
  { id: 'paper-grid', name: 'Paper Grid', emoji: '⌗', keywords: ['paper', 'wireframe', 'grid', 'planning'] },
  { id: 'blank-white', name: 'Blank Canvas', emoji: '□', keywords: ['blank', 'white', 'simple', 'empty'] },
];

export const characterAssets: CharacterAsset[] = [
  {
    id: 'ava',
    name: 'Ava',
    keywords: ['woman', 'adult', 'creative', 'casual'],
    defaultStyle: {
      skin: '#9b5e3c',
      hair: '#36221b',
      top: '#ff6f91',
      bottom: '#f9c74f',
      shoes: '#27243c',
      expression: 'happy',
      pose: 'stand',
      view: 'front',
    },
  },
  {
    id: 'noah',
    name: 'Noah',
    keywords: ['man', 'adult', 'casual', 'designer'],
    defaultStyle: {
      skin: '#c98555',
      hair: '#241b18',
      top: '#4f86f7',
      bottom: '#5bc0a8',
      shoes: '#27243c',
      expression: 'happy',
      pose: 'stand',
      view: 'front',
    },
  },
  {
    id: 'maya',
    name: 'Maya',
    keywords: ['woman', 'adult', 'professional', 'teacher'],
    defaultStyle: {
      skin: '#6f3f2c',
      hair: '#17100d',
      top: '#7b6cff',
      bottom: '#e86a92',
      shoes: '#29243a',
      expression: 'neutral',
      pose: 'stand',
      view: 'front',
    },
  },
  {
    id: 'leo',
    name: 'Leo',
    keywords: ['man', 'adult', 'business', 'professional'],
    defaultStyle: {
      skin: '#efba8c',
      hair: '#6b432c',
      top: '#2d2a4a',
      bottom: '#7d8fa8',
      shoes: '#1e1c2e',
      expression: 'neutral',
      pose: 'point',
      view: 'front',
    },
  },
  {
    id: 'zoe',
    name: 'Zoe',
    keywords: ['girl', 'young', 'student', 'child'],
    defaultStyle: {
      skin: '#d99b72',
      hair: '#6a3827',
      top: '#ff9f43',
      bottom: '#6255f6',
      shoes: '#27243c',
      expression: 'surprised',
      pose: 'wave',
      view: 'front',
    },
  },
  {
    id: 'sam',
    name: 'Sam',
    keywords: ['boy', 'young', 'student', 'child'],
    defaultStyle: {
      skin: '#8d5135',
      hair: '#251713',
      top: '#34b8a0',
      bottom: '#ffca5c',
      shoes: '#27243c',
      expression: 'happy',
      pose: 'celebrate',
      view: 'front',
    },
  },
  {
    id: 'riley',
    name: 'Riley',
    keywords: ['person', 'adult', 'inclusive', 'casual'],
    defaultStyle: {
      skin: '#edbd91',
      hair: '#d2643d',
      top: '#38a3d1',
      bottom: '#314a72',
      shoes: '#27243c',
      expression: 'happy',
      pose: 'wave',
      view: 'front',
    },
  },
  {
    id: 'professor',
    name: 'Professor Quinn',
    keywords: ['teacher', 'scientist', 'older', 'glasses'],
    defaultStyle: {
      skin: '#d5a178',
      hair: '#d5d0c9',
      top: '#f4f0e6',
      bottom: '#3d536d',
      shoes: '#27243c',
      expression: 'happy',
      pose: 'point',
      view: 'front',
    },
  },
];

export const propAssets: PropAsset[] = [
  { id: 'laptop', name: 'Laptop', keywords: ['computer', 'work', 'technology'], aspect: 1.35 },
  { id: 'phone', name: 'Smartphone', keywords: ['mobile', 'technology', 'call'], aspect: 0.52 },
  { id: 'tablet', name: 'Tablet', keywords: ['screen', 'technology', 'device'], aspect: 0.75 },
  { id: 'lightbulb', name: 'Bright Idea', keywords: ['idea', 'innovation', 'light'], aspect: 0.72 },
  { id: 'coffee', name: 'Coffee Cup', keywords: ['drink', 'mug', 'cafe'], aspect: 1.2 },
  { id: 'book-stack', name: 'Book Stack', keywords: ['books', 'education', 'reading'], aspect: 1.2 },
  { id: 'plant', name: 'Potted Plant', keywords: ['plant', 'nature', 'office'], aspect: 0.75 },
  { id: 'desk', name: 'Desk', keywords: ['table', 'office', 'furniture'], aspect: 2.2 },
  { id: 'chair', name: 'Chair', keywords: ['seat', 'furniture', 'office'], aspect: 0.8 },
  { id: 'whiteboard', name: 'Whiteboard', keywords: ['board', 'presentation', 'office'], aspect: 1.6 },
  { id: 'chart', name: 'Growth Chart', keywords: ['graph', 'data', 'business'], aspect: 1.4 },
  { id: 'clock', name: 'Clock', keywords: ['time', 'deadline', 'schedule'], aspect: 1 },
  { id: 'camera', name: 'Camera', keywords: ['photo', 'film', 'media'], aspect: 1.25 },
  { id: 'megaphone', name: 'Megaphone', keywords: ['announce', 'sound', 'marketing'], aspect: 1.3 },
  { id: 'rocket', name: 'Rocket', keywords: ['launch', 'space', 'growth'], aspect: 0.72 },
  { id: 'car', name: 'Car', keywords: ['vehicle', 'travel', 'transport'], aspect: 2 },
  { id: 'bus', name: 'Bus', keywords: ['vehicle', 'school', 'transport'], aspect: 2.15 },
  { id: 'tree', name: 'Tree', keywords: ['nature', 'park', 'forest'], aspect: 0.8 },
  { id: 'signpost', name: 'Signpost', keywords: ['direction', 'road', 'choice'], aspect: 0.9 },
  { id: 'backpack', name: 'Backpack', keywords: ['school', 'bag', 'travel'], aspect: 0.76 },
  { id: 'gift', name: 'Gift Box', keywords: ['present', 'celebration', 'box'], aspect: 1 },
  { id: 'star', name: 'Star', keywords: ['success', 'award', 'favorite'], aspect: 1 },
  { id: 'heart', name: 'Heart', keywords: ['love', 'care', 'health'], aspect: 1 },
  { id: 'cloud', name: 'Cloud', keywords: ['cloud', 'weather', 'internet'], aspect: 1.5 },
];

export const textPresets = [
  { id: 'heading', name: 'Big heading', text: 'Big idea', size: 78, weight: 800 },
  { id: 'subheading', name: 'Subheading', text: 'Add a supporting line', size: 48, weight: 700 },
  { id: 'body', name: 'Body text', text: 'Write a short explanation here.', size: 36, weight: 500 },
  { id: 'label', name: 'Small label', text: 'LABEL', size: 26, weight: 800 },
  { id: 'quote', name: 'Pull quote', text: '“A memorable line belongs here.”', size: 44, weight: 700 },
];

export const bubblePresets = [
  { id: 'speech', name: 'Speech bubble' },
  { id: 'thought', name: 'Thought bubble' },
  { id: 'shout', name: 'Shout bubble' },
  { id: 'caption', name: 'Caption card' },
  { id: 'whisper', name: 'Whisper bubble' },
] as const;

export const shapePresets = [
  { id: 'rectangle', name: 'Rectangle' },
  { id: 'ellipse', name: 'Ellipse' },
  { id: 'star', name: 'Star' },
  { id: 'arrow', name: 'Arrow' },
  { id: 'line', name: 'Line' },
  { id: 'heart', name: 'Heart' },
] as const;

export const findCharacterAsset = (id?: string): CharacterAsset =>
  characterAssets.find((asset) => asset.id === id) ?? characterAssets[0];
